import { z } from 'zod';

import { ZodAmount } from '@Common/types/Amount';
import {
  ZodDynamicRestrictionFlagWithReason,
  ZodEmployeeRestrictionsFlag,
} from '@Common/types/bo/b2b/employees/restrictions';
import { ZodB2BPaymentMethod } from '@Common/types/funding/payment_methods';
import { ZodInstrumentsUIUniverses } from '@Common/types/instruments/universes';
import { ZodLanguageCode } from '@Common/types/language';
import { ZodProfileSavingPlanId } from '@Common/types/profile/saving_plans';
import { ZodUserAccountType } from '@Common/types/questions/account_type';
import { ZodMicaSfaAgreementStatus } from '@Common/types/questions/legal_agreements';
import { ZodCurrencySchema } from '@Common/types/zod/zod-amount';
import { ZodCountryCode } from '@Common/types/zod/zod-country-code';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodHashedIdResponse } from '@Common/types/zod/zod-hash-id';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const UserUIApprobationStates = [
  'in_review',
  'financial_profile_incomplete',
  'proof_address_required',
  'proof_address_retry',
  'application_denied',
  'approved',
  'terms_and_conditions_required',
  'tax_number_required',
  'risk_score_failed',
  'fourthline_documents_required',
  'fourthline_retry',
  'fourthline_retry_document_has_expired',
] as const;
export const ZodUserApprobationState = z.enum(UserUIApprobationStates);
export type UserApprobationState = z.infer<typeof ZodUserApprobationState>;

export const UserPOIUIApprobationStates = [
  'in_review',
  'fourthline_documents_required',
  'fourthline_retry',
  'fourthline_retry_document_has_expired',
  'approved',
] as const;
export const ZodUserPoiApprobationState = z.enum(UserPOIUIApprobationStates);
export type UserPoiApprobationState = z.infer<typeof ZodUserPoiApprobationState>;

export const UserPOAUIApprobationStates = [
  'in_review',
  'proof_address_retry',
  'proof_address_required',
  'approved',
] as const;
export const ZodUserPoaApprobationState = z.enum(UserPOAUIApprobationStates);
export type UserPoaApprobationState = z.infer<typeof ZodUserPoaApprobationState>;

export const UserSOFUIApprobationStates = [
  'in_review',
  'source_of_funds_retry',
  'source_of_funds_required',
  'approved',
] as const;
export const ZodUserSofApprobationState = z.enum(UserSOFUIApprobationStates);
export type UserSofApprobationState = z.infer<typeof ZodUserSofApprobationState>;

const InvestmentAccountB2CTypeValues = ['gia', 'pea', 'per'] as const;
export const InvestmentAccountB2CType = {
  GIA: 'gia',
  PEA: 'pea',
  PER: 'per',
} as const;

// Use ZodInvestmentAccountAllTypeEnum instead
export const ZodInvestmentAccountB2CTypeEnum = z.enum(InvestmentAccountB2CTypeValues);

// Use InvestmentAccountAllType instead
export type InvestmentAccountB2CType = z.infer<typeof ZodInvestmentAccountB2CTypeEnum>;

// TODO: https://sh4res.atlassian.net/browse/BTC-6447
// Merge with InvestmentAccountB2CType
const InvestmentAccountTypeValues = ['gia', 'mutual_fund'] as const;

export const InvestmentAccountType = {
  GIA: 'gia',
  MUTUAL_FUND: 'mutual_fund',
} as const;

export const ZodInvestmentAccountTypeEnum = z.enum(InvestmentAccountTypeValues);

export type InvestmentAccountType = z.infer<typeof ZodInvestmentAccountTypeEnum>;

export const ZodInvestmentAccountAllTypeEnum = z.enum([
  ...InvestmentAccountB2CTypeValues,
  InvestmentAccountType.MUTUAL_FUND,
  'per',
  'employee_savings',
]);
export type InvestmentAccountAllType = z.infer<typeof ZodInvestmentAccountAllTypeEnum>;

export const ZodInvestmentAccountType = z.object({
  investment_account_type: ZodInvestmentAccountTypeEnum.default('gia').optional(),
});

export const FSUUICases = ['droid', 'activity_post', 'none'] as const;
export const ZodFSUCases = z.enum(FSUUICases);
export type FSUCases = z.infer<typeof ZodFSUCases>;

export const ZodRestrictionsFlag = z.enum([
  'suspended',
  'block_money_actions',
  'block_deposit_actions',
  'block_withdrawal_actions',
  'closing',
  'closed',
  'social_ban',
]);
export type RestrictionsFlag = z.infer<typeof ZodRestrictionsFlag>;

export const ZodSavings = z.object({
  company_id: z.number().int(),
  company_name: ZodTrimmedString,
  company_logo_url: ZodTrimmedString.nullable(),
});

export const PaymentMethodRequirementsValues = ['bank_transfer_required'] as const;

export type PaymentMethodRequirements = (typeof PaymentMethodRequirementsValues)[number];

export const ZodPaymentMethodConfig = z.object({
  enabled: z.boolean(),
  available: z.boolean(),
  requirement: z.enum(PaymentMethodRequirementsValues).optional(),
  restricted_country_codes: z.array(ZodCountryCode).optional(),
});

export type PaymentMethodConfig = z.infer<typeof ZodPaymentMethodConfig>;

export const ZodPaymentMethodsConfig = z.object({
  card: ZodPaymentMethodConfig,
  'regular-bank': ZodPaymentMethodConfig,
  'apple-pay': ZodPaymentMethodConfig,
  'sepa-direct-debit': ZodPaymentMethodConfig,
});

export const ZodGetMeResponse = z.object({
  user_id: ZodHashedIdResponse,
  intercom_hmac_ios: ZodTrimmedString.nullable(),
  intercom_hmac_android: ZodTrimmedString.nullable(),
  currency: ZodCurrencySchema.nullable(),
  language: ZodLanguageCode,
  approbation_state: ZodUserApprobationState,
  free_stock_claimed: z.boolean(),
  free_crypto_claimed: z.boolean(),
  restrictions_flags: z.array(ZodRestrictionsFlag),
  us_stocks_terms_and_conditions_accepted: z.boolean(),
  crypto_terms_and_conditions_accepted: z.boolean(),
  instruments_universes: z.array(ZodInstrumentsUIUniverses),
  country_of_tax_residence: ZodTrimmedString,
  citizenship: ZodCountryCode.nullable(),
  payment_methods_config: ZodPaymentMethodsConfig,
  is_shares_staff: z.boolean(),
  email_verified: z.boolean(),
  fsu: ZodFSUCases,
  has_savings: z.boolean(),
  social_url: ZodTrimmedString.nullable(),
  is_tax_number_specified: z.boolean().optional(),
  enabled_investment_account_types: ZodInvestmentAccountAllTypeEnum.array(),
  manual_checks_approbation_states: z.object({
    poi: ZodUserPoiApprobationState.nullable(),
    poa: ZodUserPoaApprobationState.nullable(),
    sof: ZodUserSofApprobationState.nullable(),
  }),
  account_type: ZodUserAccountType,
  has_onboarded_commodities_trading: z.boolean(),
  mica_sfa_agreement_status: ZodMicaSfaAgreementStatus.nullable(),
});

export type GetMeResponse = z.infer<typeof ZodGetMeResponse>;

export const getProfileMeEndpoint = endpoint({
  method: 'GET',
  responseBody: ZodGetMeResponse,
});

export type GetProfileMeEndpoint = GetEndpointType<typeof getProfileMeEndpoint>;

const ZodProfileMeEmployeeRestrictionFlags = ZodEmployeeRestrictionsFlag.exclude([
  'block_voluntary_payments_and_withdrawals',
  'block_selected_instruments_orders',
  'block_campaigns',
]);

export type ProfileMeEmployeeRestrictionFlags = z.TypeOf<
  typeof ZodProfileMeEmployeeRestrictionFlags
>;

const ZodRestrictedArbitrageInstrument = z.object({
  instrument_id: z.number().int(),
  is_partially_restricted: z.boolean(),
});

const ZodRestrictionObject = z.object({
  restriction_flag: ZodEmployeeRestrictionsFlag.array(),
  restricted_instrument_ids: z.array(z.number().int()),
  restricted_arbitrage_instrument_ids: ZodRestrictedArbitrageInstrument.array(),
});
export type ProfileMeRestrictedArbitrageInstrument = z.TypeOf<
  typeof ZodRestrictedArbitrageInstrument
>;

const ZodProfileMeEmployeeRestrictions = z.record(ZodProfileSavingPlanId, ZodRestrictionObject);

export type ProfileMeEmployeeRestrictions = z.TypeOf<typeof ZodProfileMeEmployeeRestrictions>;

const ZodGetProfileMeEmployeeRestrictionsResponse = z.object({
  restrictions: ZodProfileMeEmployeeRestrictions,
  has_global_restriction: z.boolean(),
});
export type GetProfileMeEmployeeRestrictionsResponse = z.TypeOf<
  typeof ZodGetProfileMeEmployeeRestrictionsResponse
>;

const ZodDynamicRestrictionObject = z.object({
  restriction_flag: ZodDynamicRestrictionFlagWithReason.array(),
  restricted_instrument_ids: z.array(z.number().int()),
  restricted_arbitrage_instrument_ids: ZodRestrictedArbitrageInstrument.array(),
});

const ZodProfileMeEmployeeDynamicRestrictions = z.record(
  ZodProfileSavingPlanId,
  ZodDynamicRestrictionObject,
);

export type ProfileMeEmployeeDynamicRestrictions = z.TypeOf<
  typeof ZodProfileMeEmployeeDynamicRestrictions
>;

export const getProfileMeEmployeeRestrictionsEndpoint = endpoint({
  method: 'GET',
  queryParams: createZodParamsValidator(
    z.object({
      filter_precomputed_flags: z.boolean().optional(),
    }),
  ),
  responseBody: ZodGetProfileMeEmployeeRestrictionsResponse,
});

export type GetProfileMeEmployeeRestrictionsEndpoint = GetEndpointType<
  typeof getProfileMeEmployeeRestrictionsEndpoint
>;

const ZodGetProfileMeEmployeeDynamicRestrictionsResponse = z.object({
  restrictions: ZodProfileMeEmployeeDynamicRestrictions,
  has_global_restriction: z.boolean(),
  has_only_indirect_international_contracts: z.boolean(),
});
export type GetProfileMeEmployeeDynamicRestrictionsResponse = z.TypeOf<
  typeof ZodGetProfileMeEmployeeDynamicRestrictionsResponse
>;

export const getProfileMeEmployeeDynamicRestrictionsEndpoint = endpoint({
  method: 'GET',
  responseBody: ZodGetProfileMeEmployeeDynamicRestrictionsResponse,
});

export type GetProfileMeEmployeeDynamicRestrictionsEndpoint = GetEndpointType<
  typeof getProfileMeEmployeeDynamicRestrictionsEndpoint
>;

const ZodGetProfileMeEmployeeSavingsResponse = z.object({
  has_b2b_investments: z.boolean(),
  has_confirmed_details: z.boolean(),
  is_valid_proof_of_identity_submitted: z.boolean(),
  default_deposit_method: ZodB2BPaymentMethod.nullable(),
  voluntary_payments: z.object({
    total: ZodAmount,
    limits: z.object({
      order: ZodAmount,
      total: ZodAmount,
    }),
  }),
  er_voluntary_payments: z.object({
    total: ZodAmount,
    limits: z.object({
      identity_document_required_threshold: ZodAmount,
      source_of_funds_required_threshold: ZodAmount,
    }),
  }),
  current_user_age: z.number().int().positive().nullable(),
  are_historical_statements_visible: z.boolean().optional(),
});

export type GetProfileMeEmployeeSavingsResponse = z.infer<
  typeof ZodGetProfileMeEmployeeSavingsResponse
>;

export const getProfileMeEmployeeSavingsEndpoint = endpoint({
  method: 'GET',
  responseBody: ZodGetProfileMeEmployeeSavingsResponse,
});

export type GetProfileMeEmployeeSavingsEndpoint = GetEndpointType<
  typeof getProfileMeEmployeeSavingsEndpoint
>;

const ZodGetProfileMeEmployeeSavingsCompaniesResponse = z.object({
  companies: z.array(
    z.object({
      support_phone_number: ZodTrimmedString.nullish(),
      support_email: ZodTrimmedString.nullish(),
      affiliated_at: z.iso.datetime(),
      filib_url: z.url().nullable(),
      ...ZodSavings.shape,
    }),
  ),
});

export type GetProfileMeEmployeeSavingsCompaniesResponse = z.infer<
  typeof ZodGetProfileMeEmployeeSavingsCompaniesResponse
>;

export type ProfileSavingsCompany = z.TypeOf<
  typeof ZodGetProfileMeEmployeeSavingsCompaniesResponse
>['companies'][number];

export const getProfileMeEmployeeSavingsCompaniesEndpoint = endpoint({
  method: 'GET',
  responseBody: ZodGetProfileMeEmployeeSavingsCompaniesResponse,
});

export type GetProfileMeEmployeeSavingsCompaniesEndpoint = GetEndpointType<
  typeof getProfileMeEmployeeSavingsCompaniesEndpoint
>;

export const ZodCompanyLeiCodeState = z.enum(['approved', 'rejected', 'required', 'in_review']);

export type CompanyLeiCodeState = z.infer<typeof ZodCompanyLeiCodeState>;

export const getProfileMeCompanyEndpoint = endpoint({
  method: 'GET',
  responseBody: z.object({
    lei_code: z.object({
      value: ZodTrimmedString.nullable(),
      state: ZodCompanyLeiCodeState,
    }),
  }),
});

export type GetProfileMeCompanyEndpoint = GetEndpointType<typeof getProfileMeCompanyEndpoint>;

// copy of B2bEmployeeInvestorProfile from '@Src/db/generated_types/enums.ts'
export const ZodRoboAdvisorProduct = z.enum(['careful', 'balanced', 'dynamic']);

export const getProfileMeEmployeeSavingsMetricsEndpoint = endpoint({
  method: 'GET',
  responseBody: z.object({
    total_deductible_voluntary_payments_amount: ZodAmount,
    robo_advisor_investor_profile: ZodRoboAdvisorProduct.nullable(),
  }),
});

export type GetProfileMeEmployeeSavingsMetricsEndpoint = GetEndpointType<
  typeof getProfileMeEmployeeSavingsMetricsEndpoint
>;
